import React from 'react';
import { Button, Popover, Typography } from 'antd';
import { MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { CustomerContact, normalizeCustomerContacts } from '../../utils/customerContacts';
import './CustomerContactCell.css';

const { Text } = Typography;

interface CustomerContactCellProps {
  contacts?: CustomerContact[] | null;
  contactPerson?: string | null;
  phone?: string | null;
  emptyValue?: string;
}

const CustomerContactCell: React.FC<CustomerContactCellProps> = ({
  contacts,
  contactPerson,
  phone,
  emptyValue = '—'
}) => {
  const { t } = useTranslation('common');
  const normalizedContacts = normalizeCustomerContacts(contacts, contactPerson, phone);
  const primaryContact =
    normalizedContacts.find((contact) => contact.isPrimary === true) ||
    normalizedContacts[0] ||
    null;
  const extraContactsCount = Math.max(normalizedContacts.length - 1, 0);
  const contactRows = normalizedContacts.map((contact, index) => ({
    ...contact,
    key: `${contact.name || 'contact'}-${contact.phone || 'phone'}-${index}`,
    isPrimaryRow: contact === primaryContact
  }));

  if (!primaryContact?.name && !primaryContact?.phone) {
    return <Text type="secondary">{emptyValue}</Text>;
  }

  const contactPopover = (
    <div className="customers-contact-popover">
      <Text strong className="customers-contact-popover__title">
        {t('contacts.title')}
      </Text>
      <div className="customers-contact-popover__list">
        {contactRows.map((contact) => (
          <div key={contact.key} className="customers-contact-popover__item">
            <div className="customers-contact-popover__header">
              <Text strong>{contact.name || emptyValue}</Text>
              {contact.isPrimaryRow && (
                <span className="customers-contact-popover__primary">
                  {t('contacts.primary')}
                </span>
              )}
            </div>
            {contact.phone && (
              <span className="customers-contact-popover__detail">
                <PhoneOutlined />
                {contact.phone}
              </span>
            )}
            {contact.email && (
              <span className="customers-contact-popover__detail">
                <MailOutlined />
                {contact.email}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <span className="customers-contact-cell">
      <span className="customers-contact-summary">
        <span className="customers-contact-lines">
          {primaryContact?.name ? (
            <span className="customers-contact-primary">{primaryContact.name}</span>
          ) : (
            <Text type="secondary">{emptyValue}</Text>
          )}
        </span>
        {extraContactsCount > 0 && (
          <Popover
            content={contactPopover}
            placement="rightTop"
            trigger="click"
            overlayClassName="customers-contact-popover-overlay"
          >
            <Button
              type="text"
              size="small"
              className="customers-contact-extra"
              aria-label={t('contacts.showAdditional')}
            >
              +{extraContactsCount}
            </Button>
          </Popover>
        )}
      </span>
      {primaryContact?.phone ? (
        <span className="customers-contact-secondary">
          <PhoneOutlined />
          {primaryContact.phone}
        </span>
      ) : (
        <Text type="secondary">{emptyValue}</Text>
      )}
    </span>
  );
};

export default CustomerContactCell;
